// «Programar este mensaje» — WHEN to send what is in the box (port of
// ios/Sources/Views/ScheduleMessageSheet.swift, Android's ScheduleMessageSheet). Reached from the
// send button's right-click / long-press. The three quick answers first, the picker last: almost
// every scheduled message is "not right now", "this evening" or "first thing tomorrow". The same
// sheet reschedules a row of the scheduled list.
import { useMemo, useState } from 'react';
import * as ScheduleOptions from '../../../core/scheduleOptions';
import { scheduledWhenLabel } from '../../../core/scheduledPost';
import { civil, localTimeZone } from '../../../core/time';
import { epochForCivil } from '../../../core/zonedTime';
import { t } from '../../../i18n/i18n';
import { Dialog } from '../../kit/Kit';
import './scheduled.css';

function optionLabel(kind: ScheduleOptions.Kind): string {
  switch (kind) {
    case 'inAnHour':
      return t('schedule.option.inAnHour');
    case 'thisEvening':
      return t('schedule.option.thisEvening');
    case 'tomorrowMorning':
      return t('schedule.option.tomorrow');
    case 'nextMondayMorning':
      return t('schedule.option.nextMonday');
  }
}

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

export function ScheduleSheet({ onSchedule, onClose, now = Date.now() }: { onSchedule(atMillis: number): void; onClose(): void; now?: number }) {
  const zone = localTimeZone();
  const options = useMemo(() => ScheduleOptions.options(now, zone), [now, zone]);
  const [custom, setCustom] = useState<{ date: string; time: string } | null>(null);

  // Seeded from the first offered time, never from `now`: a picker that opens on this very moment
  // opens on a moment that has already passed, so the first thing the reader sees is a refusal.
  const openCustom = () => {
    const c = civil(options[0]?.atMillis ?? now, zone);
    setCustom({ date: `${c.year}-${pad(c.month)}-${pad(c.day)}`, time: `${pad(c.hour)}:${pad(c.minute)}` });
  };

  const chosen = useMemo(() => {
    if (!custom) return null;
    const [y, m, d] = custom.date.split('-').map(Number);
    const [hh, mm] = custom.time.split(':').map(Number);
    if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;
    // Noon of the picked day is "any instant inside it"; `instant` resolves the clock and rounds
    // to the server's five-minute sweep.
    return ScheduleOptions.instant(epochForCivil(y, m, d, 12, 0, 0, zone), hh, mm, zone);
  }, [custom, zone]);
  const problem = chosen == null ? 'inThePast' : ScheduleOptions.problem(chosen, now);
  const sentence = problem === 'inThePast' ? t('schedule.error.past') : problem === 'tooSoon' ? t('schedule.error.tooSoon') : scheduledWhenLabel(chosen!, now);

  const pick = (at: number) => {
    onSchedule(at);
    onClose();
  };

  return (
    <Dialog
      title={t('schedule.title')}
      onClose={onClose}
      width={420}
      footer={
        custom ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setCustom(null)}>
              {t('schedule.dismiss')}
            </button>
            <button type="button" className="btn btn-primary" disabled={problem != null} onClick={() => chosen != null && pick(chosen)}>
              {t('schedule.confirm')}
            </button>
          </>
        ) : undefined
      }
    >
      {custom ? (
        <div className="sched-custom">
          <input type="date" className="text-input" aria-label={t('schedule.title')} value={custom.date} onChange={(e) => setCustom({ ...custom, date: e.target.value })} />
          <input type="time" className="text-input" aria-label={t('schedule.title')} step={300} value={custom.time} onChange={(e) => setCustom({ ...custom, time: e.target.value })} />
          <p className={`sched-sentence${problem ? ' problem' : ''}`} role="status">
            {sentence}
          </p>
        </div>
      ) : (
        <div className="sched-options">
          {options.map((o) => (
            <button key={o.atMillis} type="button" className="sched-option" onClick={() => pick(o.atMillis)}>
              <span className="sched-option-label">{optionLabel(o.kind)}</span>
              <span className="sched-option-detail">{scheduledWhenLabel(o.atMillis, now)}</span>
            </button>
          ))}
          <button type="button" className="sched-option" onClick={openCustom}>
            <span className="sched-option-label">{t('schedule.option.custom')}</span>
          </button>
        </div>
      )}
    </Dialog>
  );
}
